import React, { useEffect } from "react";

import {
  faExclamationCircle,
  faPen,
  faPlus,
  faRobot,
  faRotateRight,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { times } from "lodash-es";
import { useNavigate } from "react-router-dom";

import {
  useAgents,
  useDeleteAgent,
  usePrefetchAgentModels,
} from "src/agents/queries/management";
import { Agent } from "src/agents/types";
import { UpsertAgentModal } from "src/agents/UpsertAgentModal";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  ResourceCard,
  ResourceCardSkeleton,
  ResourceCardsContainer,
} from "src/base-components/ResourceCard";
import {
  ResourceStatusPill,
  ResourceStatusPillContainer,
} from "src/base-components/ResourceStatusPill";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { SubHeader } from "src/flow/SubHeader";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import { getUrlToAgentVersionsPage } from "src/router/urls";
import { logger } from "src/utils/logger";
import { getApiErrorDetail, pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentsPage: React.FC = () => {
  useDocumentTitle(["Agents"]);
  const {
    isOpen: isUpsertModalOpen,
    data: agentToEdit,
    openModal: openUpsertModal,
    afterLeave,
  } = useModal<Agent | undefined>();

  // Prefetch available models for agent configuration
  const prefetchModels = usePrefetchAgentModels();
  useEffect(() => {
    prefetchModels();
  }, [prefetchModels]);

  return (
    <div className="flex h-full flex-col" data-loc="agents-page">
      <PageHeader title="Agents" />
      <SubHeader title={null}>
        <SubHeader.Button
          dataLoc="create-agent-button"
          icon={faPlus}
          tooltip="Create agent"
          onClick={() => openUpsertModal()}
        />
      </SubHeader>

      <div className="decideScrollbar h-full min-w-[300px] overflow-hidden p-5">
        <AgentsList onEditAgent={(agent) => openUpsertModal(agent)} />
      </div>

      <UpsertAgentModal
        agent={agentToEdit}
        open={isUpsertModalOpen}
        onClose={afterLeave}
      />
    </div>
  );
};

const AgentsList: React.FC<{
  onEditAgent: (agent: Agent) => void;
}> = ({ onEditAgent }) => {
  const { data: agents, isError, isLoading, refetch, isFetching } = useAgents();
  const { mutateAsync: deleteAgent } = useDeleteAgent();
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId: string }>();
  const {
    isOpen: isDeleteModalOpen,
    data: agentToDelete,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal<Agent>();

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;

    try {
      await deleteAgent(agentToDelete.id);
      toastActions.success({
        title: "Agent deleted successfully",
      });
      closeDeleteModal();
    } catch (error) {
      logger.error("Failed to delete agent", error);

      if (isAxiosError(error)) {
        if (error.response?.status === 409) {
          toastActions.failure({
            title: "Failed to delete agent",
            description:
              "The Agent has published versions and can't be deleted",
          });
          return;
        }

        toastActions.failure({
          title: "Failed to delete agent",
          description: getApiErrorDetail(error),
        });
        return;
      }

      toastActions.failure({
        title: "Failed to delete agent",
        description: "An unknown error occurred",
      });
    }
  };

  if (!isLoading && isError) {
    return (
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
            disabled: isFetching,
          }}
          dataLoc="agents-error-state"
          description="We're having trouble loading agents. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading agents"
          icon={faExclamationCircle}
          variant="error"
        />
      </WhitePane>
    );
  }

  if (!isLoading && (!agents || agents.length === 0)) {
    return (
      <WhitePane>
        <EmptyState
          dataLoc="agents-empty-state"
          description="There are no agents found in this workspace yet."
          headline="No agents found"
          icon={faRobot}
        />
      </WhitePane>
    );
  }

  return (
    <>
      <ResourceCardsContainer>
        {isLoading
          ? times(3, (i) => <ResourceCardSkeleton key={i} />)
          : agents?.map((agent) => {
              const options: OptionsDropdownElement[] = [
                {
                  key: "Edit name & description",
                  icon: faPen,
                  action: () => onEditAgent(agent),
                },
                {
                  key: "Delete",
                  icon: faTrashCan,
                  action: () => openDeleteModal(agent),
                },
              ];

              return (
                <ResourceCard
                  key={agent.id}
                  dataLoc={`agent-card-${agent.id}`}
                  description={agent.description}
                  title={<div className="line-clamp-2">{agent.name}</div>}
                  onClick={() =>
                    navigate(getUrlToAgentVersionsPage(orgId, wsId, agent.id))
                  }
                >
                  <div>
                    <div className="flex items-end justify-between">
                      <ResourceStatusPillContainer>
                        <div className="mt-4 flex flex-wrap items-center gap-1">
                          {agent.published_versions_count! > 0 && (
                            <ResourceStatusPill
                              key="published"
                              color="green"
                              tooltip={{
                                title: `${pluralize(agent.published_versions_count!, "published version")}`,
                              }}
                              value={agent.published_versions_count!}
                            />
                          )}
                          {agent.draft_versions_count! > 0 && (
                            <ResourceStatusPill
                              key="draft"
                              color="yellow"
                              tooltip={{
                                title: `${pluralize(agent.draft_versions_count!, "draft version")}`,
                              }}
                              value={agent.draft_versions_count!}
                            />
                          )}
                        </div>
                      </ResourceStatusPillContainer>
                      <EllipsisOptionsDropdown
                        buttonClassName="opacity-0 group-hover/resource-card:opacity-100 w-6 h-6"
                        dataLoc={`agent-options-${agent.id}`}
                        elements={options}
                      />
                    </div>
                  </div>
                </ResourceCard>
              );
            })}
      </ResourceCardsContainer>

      <ConfirmationModal
        confirmationButtonText="Delete"
        description={`Are you sure you want to delete "${agentToDelete?.name}"? This action cannot be undone.`}
        open={isDeleteModalOpen}
        title="Delete agent"
        variant="danger"
        onClose={closeDeleteModal}
        onConfirm={handleDeleteAgent}
      />
    </>
  );
};
